"use client";

import { recordLastAuthMethodFromSession } from "@/components/auth-ui/lastAuthMethod";
import { clearResponseCache } from "@/components/responses/table/responseCache";
import { clearActiveChatIds } from "@/components/unified-chat/core/activeChatStorage";
import { clearDraftTexts } from "@/components/unified-chat/core/draftTextStorage";
import { fireAndForget } from "@/lib/vercelUtils";
import { createBrowserClient } from "@/utils/supabase/client";
import type { Database } from "@/utils/types";
import type { SupabaseClient, User } from "@supabase/supabase-js";
import { isAuthSessionMissingError } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";

type SupabaseContext = {
  supabase: SupabaseClient<Database>;
  user: User | null;
};

const Context = createContext<SupabaseContext | undefined>(undefined);

export default function SupabaseProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [supabase] = useState(() =>
    createBrowserClient({ fetchWithRetry: true })
  );
  const [user, setUser] = useState<User | null>(null);
  const router = useRouter();
  const queryClient = useQueryClient();

  // Layout effect, so the tokens leave the URL before PostHog inits in a passive effect.
  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    // IDP-initiated SSO: Supabase SAML ACS can redirect directly to any page
    // (e.g. /dashboard) with tokens in the URL fragment. Since detectSessionInUrl
    // is disabled, we need to manually redirect to /auth so the dedicated
    // useParseImplicitFlowAccessToken hook can call setSession().
    if (
      window.location.hash.includes("access_token=") &&
      !window.location.pathname.startsWith("/auth")
    ) {
      const { hash, pathname, search } = window.location;
      window.history.replaceState(
        window.history.state,
        document.title,
        pathname + search
      );
      window.location.href = "/auth?from=callback" + hash;
    }
  }, []);

  useEffect(() => {
    let isChecking = false;
    // A check already in flight may have read the session before it was lost.
    let hasPendingCheck = false;
    // A queued check must not run after cleanup, since it would read a stale user.
    let isDisposed = false;
    const checkSession = () => {
      if (isChecking) {
        hasPendingCheck = true;
        return;
      }
      isChecking = true;
      void supabase.auth
        .getUser()
        .then(({ error }) => {
          if (isAuthSessionMissingError(error) && user !== null) {
            // Session is truly gone (cleared cookies, expired refresh token).
            // A full page load: a client refresh can replay a redirect Next
            // kept from before, and leave a blank /auth.
            console.log("Not authenticated");
            window.location.reload();
          }
          // Don't setUser here — onAuthStateChange already keeps user in sync.
          // Calling setUser with a new object ref would trigger unnecessary
          // re-renders and re-register this effect (user is in the dep array).
        })
        .finally(() => {
          isChecking = false;
          if (!hasPendingCheck || isDisposed) return;
          hasPendingCheck = false;
          checkSession();
        });
    };
    const handle_visibility_change = () => {
      if (document.visibilityState !== "visible") {
        return;
      }
      checkSession();
    };

    document.addEventListener("visibilitychange", handle_visibility_change);
    window.addEventListener("focus", handle_visibility_change);
    return () => {
      isDisposed = true;
      document.removeEventListener(
        "visibilitychange",
        handle_visibility_change
      );
      window.removeEventListener("focus", handle_visibility_change);
    };
  }, [router, supabase, user]);

  useEffect(() => {
    void (async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      setUser(user);
    })();
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      console.log("Auth event. User:", session?.user?.email, event);
      setUser(session?.user ?? null);
      // Only refresh on meaningful auth changes. TOKEN_REFRESHED is just a background
      // token renewal and should not cause a page refresh, as that can desync client
      // state (e.g., during an interview, causing fallback to first question).
      if (event === "SIGNED_OUT") {
        void clearResponseCache();
        queryClient.clear();
        clearDraftTexts();
        clearActiveChatIds();
      }
      // INITIAL_SESSION is included because server-side PKCE callbacks
      // (OAuth, SSO) land on a fresh page load and never emit SIGNED_IN.
      if ((event === "SIGNED_IN" || event === "INITIAL_SESSION") && session) {
        fireAndForget(recordLastAuthMethodFromSession(supabase));
      }
      if (
        event !== "INITIAL_SESSION" &&
        event !== "SIGNED_IN" &&
        event !== "TOKEN_REFRESHED"
      ) {
        router.refresh();
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [router, supabase, queryClient]);

  return (
    <Context.Provider value={{ supabase, user }}>
      <>{children}</>
    </Context.Provider>
  );
}

export const useSupabase = () => {
  const context = useContext(Context);

  if (context === undefined) {
    throw new Error("useSupabase must be used inside SupabaseProvider");
  }

  return context;
};
